import React, { useMemo, useState, useCallback } from 'react'
import { Button, Icon, hooks } from '@adc/ui-core'
import classNames from 'classnames/bind'
import cls from './styles.scss'
import {
  DISMISS_COOKIE_KEY,
  parseAnnouncement,
  fingerprintAnnouncement,
  isAnnouncementDismissed,
} from './announcement'
import type {
  Announcement,
  AnnouncementTheme,
  AnnouncementCta,
} from './announcement'

const cx = classNames.bind(cls)

const { useIsomorphicLayoutEffect } = hooks

// Keep in sync with $announcement-banner-height in styles.scss.
const BANNER_HEIGHT_PX = 60

const readDismissedFingerprint = (): string | null => {
  try {
    const match = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${DISMISS_COOKIE_KEY}=`))
    return match ? decodeURIComponent(match.split('=')[1]) : null
  } catch {
    return null
  }
}

const writeDismissedFingerprint = (value: string): void => {
  try {
    // 1 year, root path so every app on the domain shares the dismissal.
    document.cookie = `${DISMISS_COOKIE_KEY}=${encodeURIComponent(
      value
    )}; path=/; max-age=31536000; SameSite=Lax`
  } catch {
    // cookie unavailable — dismissal is best-effort
  }
}

const clearDismissedFingerprint = (): void => {
  try {
    document.cookie = `${DISMISS_COOKIE_KEY}=; path=/; max-age=0; SameSite=Lax`
  } catch {
    // best-effort
  }
}

export interface AnnouncementBannerProps {
  /** Raw value of the `fe.resi.announcement-banner` LD flag. */
  flagValue?: string | Record<string, unknown>
  elmId?: string
  /**
   * Dismissal fingerprint read from the cookie server-side. When provided, the
   * banner renders hidden on the first paint (no flash). Optional — without it
   * the banner reconciles from the cookie client-side after mount (one-frame
   * flash for already-dismissed users).
   */
  dismissedFingerprint?: string
  /**
   * Called whenever the banner's rendered visibility changes. Lets a parent
   * (e.g. the app shell) collapse reserved header space when hidden/dismissed.
   */
  onVisibilityChange?: (visible: boolean) => void
}

/**
 * Temporal top-level announcement banner driven by the
 * `fe.resi.announcement-banner` LaunchDarkly flag. Replaces the legacy
 * maintenance banner.
 */
const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({
  flagValue,
  elmId = 'announcement_banner',
  dismissedFingerprint,
  onVisibilityChange,
}) => {
  const announcement = useMemo(() => parseAnnouncement(flagValue), [flagValue])

  const currentFingerprint = useMemo(
    () => (announcement ? fingerprintAnnouncement(announcement) : null),
    [announcement]
  )

  // Seed from the server-provided fingerprint so SSR + first client paint agree
  // (no flash when the app passes it; defaults to visible otherwise).
  const [dismissed, setDismissed] = useState<boolean>(() =>
    isAnnouncementDismissed(flagValue, dismissedFingerprint)
  )

  useIsomorphicLayoutEffect(() => {
    if (currentFingerprint === null) return
    const stored = readDismissedFingerprint()
    if (stored === currentFingerprint) {
      setDismissed(true)
    } else if (stored !== null) {
      // Stale dismissal from a prior announcement — clear and keep showing.
      clearDismissedFingerprint()
      setDismissed(false)
    }
  }, [currentFingerprint])

  const onDismiss = useCallback(() => {
    if (currentFingerprint !== null) {
      writeDismissedFingerprint(currentFingerprint)
    }
    setDismissed(true)
  }, [currentFingerprint])

  const visible =
    announcement !== null && !(announcement.dismissible && dismissed)

  useIsomorphicLayoutEffect(() => {
    onVisibilityChange?.(visible)

    // Expose the banner's height as a CSS var so consumers with their own
    // sticky/fixed elements (e.g. PDP's sticky sub-nav) can offset by it.
    // Set to the banner height when visible, 0 otherwise.
    try {
      document.documentElement.style.setProperty(
        '--adc-announcement-banner-height',
        visible ? `${BANNER_HEIGHT_PX}px` : '0px'
      )
    } catch {
      // no document (SSR) — CSS var only matters client-side
    }
  }, [visible, onVisibilityChange])

  if (!announcement) return null
  if (announcement.dismissible && dismissed) return null

  return (
    <div
      data-elm-id={elmId}
      className={cx('banner', announcement.theme, {
        dismissible: announcement.dismissible,
      })}
      role='region'
      aria-label='Announcement'
    >
      <div className={cx('content')}>
        <span className={cx('message')} data-elm-id={`${elmId}_message`}>
          {announcement.message}
        </span>
        {!!announcement.cta && (
          <Button
            role='primary'
            size='small'
            target='_self'
            elmId={`${elmId}_cta`}
            link={announcement.cta.url}
            className={cx('cta')}
          >
            {announcement.cta.text}
          </Button>
        )}
      </div>
      {announcement.dismissible && (
        <button
          type='button'
          className={cx('dismiss')}
          onClick={onDismiss}
          aria-label='Dismiss announcement'
          data-elm-id={`${elmId}_dismiss`}
        >
          <Icon
            name='xmark'
            iconStyle='regular'
            size='large'
            aria-hidden='true'
          />
        </button>
      )}
    </div>
  )
}

export default AnnouncementBanner
export {
  DISMISS_COOKIE_KEY,
  parseAnnouncement,
  fingerprintAnnouncement,
  isAnnouncementDismissed,
}
export type { Announcement, AnnouncementTheme, AnnouncementCta }
